import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import StylePacksTab from './style-packs-tab';
import StylePackInfo from './style-pack-info';
import { GreenroomState as GS } from '../../stores/types/types';
import { META_MENU_ROUTES, META_MENU_STATES } from '../../stores/types/tabs';
import { closeMetaMenu, setMetaMenuRoute } from '../../stores/actions/tabs';
import { scrollToStylePackId, setDisplayedStylePackInfo } from '../../stores/actions/user-interface';
import classNames from 'classnames';
import { StylePackData } from '../types/stylepacks';
import StylePacksLoadingTabSkeleton from './style-packs-loading-tab-skeleton';
import { ShotModalStylepackDrawer } from '../modal/templates/shot-builder/shot-builder';

interface StylePackTabContainerProps {
    shouldRenderInfo: boolean;
    drawerState: string;
    shotModalStylepackDrawer?: ShotModalStylepackDrawer;
    setShotModalStylepackDrawer?: (stylepackDrawer: ShotModalStylepackDrawer) => void;
}

export default function StylePackTabContainer(
    {
        shouldRenderInfo,
        drawerState,
        shotModalStylepackDrawer,
        setShotModalStylepackDrawer = () => {},
    }: StylePackTabContainerProps
) {
    const dispatch = useDispatch();

    // prefer values from shotModalStylepackDrawer if present
    const stylePack = shotModalStylepackDrawer?.stylePack ?? useSelector((s: GS) => s.UserInterfaceReducer.displayedStylePackInfo ?? undefined);
    const selectedItem = shotModalStylepackDrawer?.stylePackItem ?? useSelector((s: GS) => s.UserInterfaceReducer.scrollToStylePackId);
    const showSkeleton = drawerState !== META_MENU_STATES.OPEN && !shouldRenderInfo;

    const switchToStylePackInfo = (nStylePack: StylePackData, id: string) => {
        if (shotModalStylepackDrawer) {
            setShotModalStylepackDrawer({
                ...shotModalStylepackDrawer,
                showInfo: true,
                stylePack: nStylePack,
                stylePackItem: id,
            });
        } else {
            dispatch(setDisplayedStylePackInfo(nStylePack));
            dispatch(scrollToStylePackId(id));
            dispatch(setMetaMenuRoute(META_MENU_ROUTES.STYLE_PACK_INFO));
        }
    }

    const switchToStylePacksMain = () => {
        if (shotModalStylepackDrawer) {
            setShotModalStylepackDrawer({
                ...shotModalStylepackDrawer,
                showInfo: false,
                stylePack: null,
            });
        } else {
            dispatch(setMetaMenuRoute(META_MENU_ROUTES.STYLE_PACKS));
            dispatch(setDisplayedStylePackInfo(null));
        }
    }

    const closeStylePackTab = () => {
        if (shotModalStylepackDrawer) {
            setShotModalStylepackDrawer({
                ...shotModalStylepackDrawer,
                state: META_MENU_STATES.CLOSING,
            });
        } else {
            dispatch(closeMetaMenu());
        }
    }

    if (showSkeleton) {
        return <StylePacksLoadingTabSkeleton/>;
    }

    // we always render the tab (undernearth) so that it does not re-load 
    // all the carousels when the individual style pack's info is closed.
    return <>
        <StylePacksTab 
            setStylePackInfo={switchToStylePackInfo} 
            closeStylePackTab={closeStylePackTab} 
        />
        {shouldRenderInfo ? <div
            className={classNames('style-pack-info-animation-container', {
                'sp-render-in': shouldRenderInfo,
                'sp-render-out': !shouldRenderInfo,
            })}
        >
            <StylePackInfo
                stylePack={stylePack}
                selectedItem={selectedItem}
                goBack={switchToStylePacksMain}
                closeStylePackTab={closeStylePackTab}
            />
        </div> : null}
    </>
}